# Route: /materials/classes/:id
# Read-only detail view for a material class (ItemClass reference data).
# Audience: MES Engineers (md+), blocked on sm — same audience as class-list.
# Permission: ItemClass:read (GET /api/items/classes/{classId})
# Feature: F75 #134 (AC-C3) — list rows navigate here via class-list row_action.

page-class-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.class_detail.meta_title
    route: /materials/classes/:id
    layout: shell
    auth: required
  data:
    itemClass:
      model: ItemClass
      key: $route.id
      required: true
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - class-detail-sm-notice
    - class-detail-content

# ── sm viewport notice ───────────────────────────────────────────────────────

class-detail-sm-notice:
  type: SmNotice
  content: $t.common.sm_notice_class

# ── Main content (hidden on sm) ──────────────────────────────────────────────

class-detail-content:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    responsive:
      sm:
        display: none
  children:
    - class-detail-header
    - class-detail-info

# ── Header: back-to-list (outlined secondary, from back_route) + class name ──
# Read-only page: no lifecycle actions. The PageHeaderTransform renders the
# back_route as a secondary (outlined) "← Back" button ($t.common.btn_back).

class-detail-header:
  type: PageHeader
  back_route: /materials/classes
  # Composed title: "Class — {name}". title_prefix is an i18n key; the emitter
  # owns the em-dash, the layout, and the value truncation.
  title_prefix: $t.class_detail.title_prefix
  bind:
    content: itemClass.name

# ── Summary card: label/value grid per patterns.md § Detail page composition ─
# Note: ItemClassDto exposes no version/status or created/updated timestamps,
# so this card shows the full read surface of GET /api/items/classes/{id}.

class-detail-info:
  type: Card
  spatial:
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - class-info-grid

class-info-grid:
  type: Grid
  spatial:
    columns: label-value
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
  children:
    - class-info-name-label
    - class-info-name-value
    - class-info-id-label
    - class-info-id-value
    - class-info-nature-label
    - class-info-nature-value
    - class-info-tracking-label
    - class-info-tracking-value
    - class-info-parent-label
    - class-info-parent-value
    - class-info-description-label
    - class-info-description-value

class-info-name-label:
  type: Text
  content: $t.class_detail.label_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

class-info-name-value:
  type: Text
  bind:
    content: itemClass.name
  tokens:
    font_size: $font.size.md
    font_weight: "600"

class-info-id-label:
  type: Text
  content: $t.class_detail.label_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

class-info-id-value:
  type: Text
  bind:
    content: itemClass.id
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

class-info-nature-label:
  type: Text
  content: $t.class_detail.label_nature
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

class-info-nature-value:
  type: Text
  bind:
    content: itemClass.itemNature
  tokens:
    font_size: $font.size.md
    font_weight: "600"

class-info-tracking-label:
  type: Text
  content: $t.class_detail.label_tracking
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

class-info-tracking-value:
  type: Text
  bind:
    content: itemClass.defaultTrackingMode
  tokens:
    font_size: $font.size.md
    font_weight: "600"

class-info-parent-label:
  type: Text
  content: $t.class_detail.label_parent
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

class-info-parent-value:
  type: Text
  bind:
    content: itemClass.parentClassId
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    color: $color_scheme.light.color.on-surface-muted
    font_weight: "600"

class-info-description-label:
  type: Text
  content: $t.class_detail.label_description
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

class-info-description-value:
  type: Text
  bind:
    content: itemClass.description
  tokens:
    font_size: $font.size.md
    font_weight: "600"
